import dayjs from "@calcom/dayjs";

/**
 * Ghost Booking slot grid — mirrors the config in
 * deploy/caldiy/GHOST-BOOKING-PLAN.md ("Все числа сетки — конфиг, не
 * константы в коде"): GHOST_SLOT_START/END, GHOST_LUNCH_START/END,
 * GHOST_SLOT_MINUTES, GHOST_DAYS_AHEAD, GHOST_WEEKDAYS_ONLY. The backend
 * (h/booking/) keeps its own copy of the same numbers; this file is the
 * frontend's, kept as one config object instead of scattered magic numbers.
 */
export type GhostSlotConfig = {
  /** GHOST_SLOT_START */
  startHour: number;
  /** GHOST_SLOT_END */
  endHour: number;
  /** GHOST_LUNCH_START */
  lunchStartHour: number;
  /** GHOST_LUNCH_END */
  lunchEndHour: number;
  /** GHOST_SLOT_MINUTES */
  slotMinutes: number;
  /** GHOST_DAYS_AHEAD */
  daysAhead: number;
  /** GHOST_WEEKDAYS_ONLY */
  weekdaysOnly: boolean;
};

export const GHOST_SLOT_CONFIG: GhostSlotConfig = {
  startHour: 9,
  endHour: 19,
  lunchStartHour: 13,
  lunchEndHour: 15,
  slotMinutes: 60,
  daysAhead: 14,
  weekdaysOnly: true,
};

export type GhostSlot = {
  /** Local hour of day in the visitor's timezone, e.g. 9, 10, ... 18. */
  hour: number;
  /** "09:00" — local label for the hour button. */
  label: string;
  /** UTC instant, formatted exactly like the contract example ("...T10:00:00Z"), no milliseconds. */
  startUtcIso: string;
};

export type GhostDay = {
  /** "2026-09-07" in the visitor's timezone — stable key for day selection. */
  dateKey: string;
  /** dayjs .day(): 0 = Sunday ... 6 = Saturday. */
  weekday: number;
  slots: GhostSlot[];
};

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** Hours of day that are open slots (lunch window excluded) — 09,10,11,12,15,16,17,18 for the default config. */
export function ghostSlotHours(config: GhostSlotConfig = GHOST_SLOT_CONFIG): number[] {
  const stepHours = Math.max(1, config.slotMinutes / 60);
  const hours: number[] = [];
  for (let hour = config.startHour; hour < config.endHour; hour += stepHours) {
    if (hour >= config.lunchStartHour && hour < config.lunchEndHour) continue;
    hours.push(hour);
  }
  return hours;
}

/**
 * Builds the visible day/slot grid for a visitor timezone. Days with zero
 * remaining slots (weekend, or every hour already in the past) are omitted
 * entirely rather than rendered empty.
 */
export function buildGhostSchedule(
  timezone: string,
  now: Date = new Date(),
  config: GhostSlotConfig = GHOST_SLOT_CONFIG
): GhostDay[] {
  const hours = ghostSlotHours(config);
  const nowInTz = dayjs(now).tz(timezone);
  const days: GhostDay[] = [];

  for (let offset = 0; offset < config.daysAhead; offset += 1) {
    const dayStart = nowInTz.startOf("day").add(offset, "day");
    const weekday = dayStart.day();
    if (config.weekdaysOnly && (weekday === 0 || weekday === 6)) continue;

    const slots: GhostSlot[] = [];
    for (const hour of hours) {
      const slotStart = dayStart.hour(hour).minute(0).second(0).millisecond(0);
      if (slotStart.isBefore(nowInTz)) continue; // "прошедшее время скрыто"
      slots.push({
        hour,
        label: `${pad2(hour)}:00`,
        startUtcIso: slotStart.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
      });
    }

    if (!slots.length) continue;
    days.push({ dateKey: dayStart.format("YYYY-MM-DD"), weekday, slots });
  }

  return days;
}
